import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Category, Product } from '../lib/db'
import { money } from '../lib/money'
import { useMediaQuery } from '../lib/hooks'
import { PageBack } from './PageBack'
import { blankDraft, ProductEditor, toDraft, type ProductDraft } from './ProductEditor'
import { Thumb } from './Thumb'

/**
 * Danh mục món của bộ Modern: lưới món bên trái, ô sửa món bên phải.
 *
 * Dựng menu là việc ngồi làm một lượt — thêm món, sửa giá, gán mã vạch, rồi món kế tiếp.
 * Mỗi lần phải đóng một tấm trượt rồi mở tấm khác là một nhịp bị ngắt, mà chủ quán thì
 * đang nhập 50 món liền tay.
 */

interface Props {
  categories: Category[]
  products: Product[]
  query: string
  onQuery: (q: string) => void
  onChanged: () => void | Promise<void>
  onGroups: () => void
}

export function ProductsBoard({ categories, products, query, onQuery, onChanged, onGroups }: Props) {
  const nav = useNavigate()
  const hasPane = useMediaQuery('(min-width: 1180px)')

  const [catId, setCatId] = useState<string | null>(null)
  const [draft, setDraft] = useState<ProductDraft | null>(null)

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return products.filter((p) => {
      if (q) return p.name.toLowerCase().includes(q) || (p.barcode ?? '').includes(q)
      if (catId === 'none') return p.categoryId === null
      return catId === null || p.categoryId === catId
    })
  }, [products, catId, query])

  const nameOf = (id: string | null) => categories.find((c) => c.id === id)?.name ?? 'Chưa xếp nhóm'

  // Bốn con số này là bốn câu hỏi có thật lúc dựng menu: đủ món chưa, chia nhóm chưa,
  // gán mã vạch tới đâu, và món nào bán theo cân.
  const stats = [
    { key: 'Tổng số món', value: products.length, tone: 'brand' },
    { key: 'Nhóm món', value: categories.length, tone: 'blue' },
    { key: 'Có mã vạch', value: products.filter((p) => p.barcode).length, tone: 'violet' },
    { key: 'Bán theo cân', value: products.filter((p) => p.soldByWeight).length, tone: 'amber' },
  ]

  function edit(p: Product) {
    setDraft(toDraft(p))
  }

  function addNew() {
    setDraft(blankDraft(catId === 'none' ? null : catId))
  }

  return (
    <div className="board products-board">
      <header className="page-head">
        <PageBack to={'/settings'} />
        <span className="page-icon is-violet" aria-hidden="true">
          📋
        </span>
        <div className="page-title">
          <h1>Sản phẩm</h1>
          <p>Quản lý menu, giá bán và nhóm món</p>
        </div>

        <label className="search search-wide">
          <span className="sr-only">Tìm món</span>
          <span className="search-glyph" aria-hidden="true">
            ⌕
          </span>
          <input
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Tìm tên món hoặc mã vạch…"
            inputMode="search"
          />
          <span className="search-scan" aria-hidden="true" title="Quét mã vạch được ngay">
            ▮▮▮
          </span>
        </label>

        <button
          type="button"
          className="icon-btn"
          onClick={onGroups}
          aria-label="Sửa nhóm món"
          title="Sửa nhóm món"
        >
          🗂
        </button>
      </header>

      <div className="stat-chips">
        {stats.map((s) => (
          <div key={s.key} className={`stat-chip is-${s.tone}`}>
            <span className="stat-chip-key">{s.key}</span>
            <b className="stat-chip-val num">{s.value}</b>
          </div>
        ))}
      </div>

      <div className="products-split">
        <div className="products-main">
          <div className="zone-row">
            <div className="chip-row">
              <button
                type="button"
                className="chip"
                aria-pressed={catId === null && !query}
                onClick={() => setCatId(null)}
              >
                Tất cả
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="chip"
                  aria-pressed={catId === c.id && !query}
                  onClick={() => setCatId(c.id)}
                >
                  {c.name}
                </button>
              ))}
              {products.some((p) => !p.categoryId) && (
                <button
                  type="button"
                  className="chip"
                  aria-pressed={catId === 'none' && !query}
                  onClick={() => setCatId('none')}
                >
                  Chưa xếp nhóm
                </button>
              )}
            </div>

            <div className="zone-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => nav('/settings/products/scan')}>
                📷 Chụp bảng giá
              </button>
              <button type="button" className="btn btn-brand btn-sm" onClick={addNew}>
                + Thêm món
              </button>
            </div>
          </div>

          {shown.length === 0 ? (
            <div className="empty">
              <div className="empty-icon" aria-hidden="true">
                📋
              </div>
              <p>{query ? `Không có món nào khớp "${query}".` : 'Nhóm này chưa có món nào.'}</p>
            </div>
          ) : (
            <div className="product-cards">
              {shown.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`product-card${draft?.id === p.id && hasPane ? ' is-picked' : ''}`}
                  onClick={() => edit(p)}
                >
                  <Thumb name={p.name} imageKey={p.imageKey} className="product-card-thumb" />
                  <b className="product-card-name">{p.name}</b>
                  <span className="product-card-price num">{money(p.price)}đ</span>

                  <span className="product-card-foot">
                    <span className="kind-pill">{nameOf(p.categoryId)}</span>
                    {p.barcode && (
                      <span className="tag-scan" title={`Mã vạch ${p.barcode}`}>
                        ▮▮▮
                      </span>
                    )}
                    {p.soldByWeight && (
                      <span className="tag-scan" title={`Bán theo ${p.unit}`}>
                        ⚖️
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <aside className="products-pane" aria-label="Sửa món">
          {draft && hasPane ? (
            <ProductEditor
              key={draft.id ?? 'new'}
              draft={draft}
              categories={categories}
              variant="pane"
              onClose={() => setDraft(null)}
              onSaved={async () => {
                setDraft(null)
                await onChanged()
              }}
            />
          ) : (
            <div className="pane-empty">
              <div className="empty-icon" aria-hidden="true">
                👆
              </div>
              <p>Chạm vào một món để sửa giá, đổi nhóm hoặc gán mã vạch.</p>
              <button type="button" className="btn btn-brand" onClick={addNew}>
                + Thêm món mới
              </button>
            </div>
          )}
        </aside>
      </div>

      {/*
        Màn hình hẹp không có khoang bên phải, nên ô sửa món quay lại làm tấm trượt.
        Thiếu nhánh này thì trên điện thoại chạm vào món chẳng thấy gì mở ra cả.
      */}
      {draft && !hasPane && (
        <ProductEditor
          key={draft.id ?? 'new'}
          draft={draft}
          categories={categories}
          onClose={() => setDraft(null)}
          onSaved={async () => {
            setDraft(null)
            await onChanged()
          }}
        />
      )}
    </div>
  )
}
